import { User } from "@/types/models/User/v3";
import getQueryClient from "@/utils/getQueryClient";
import {
  TRANSLATIONS_QUERY_FN,
  TRANSLATIONS_QUERY_KEY,
} from "@/view/useGetTranslatorLanguages/useGetTranslatorLanguages";
import { ILanguage } from "../TranslateDrawer.interface";

const STORAGE_KEY = "preferred-translation-lang";

export const DEFAULT_LANGUAGE = {
  name: "Choose language",
  code: "",
} as ILanguage;
/* validLanguageCode() can be used to ensure that only valid language codes are stored or retrieved by the LanguagePreferences utils. In instances of malformed data then the DEFAULT_LANGUAGE is returned. */
const validLanguage = async (
  language: ILanguage | null | undefined,
): Promise<ILanguage> => {
  const queryClient = getQueryClient();

  const VALID_LANGUAGES = await queryClient.ensureQueryData({
    queryKey: TRANSLATIONS_QUERY_KEY,
    queryFn: TRANSLATIONS_QUERY_FN,
  });

  const isValidLanguage = VALID_LANGUAGES?.some((lang: ILanguage) => {
    return lang?.code === language?.code;
  });

  return isValidLanguage && language ? language : DEFAULT_LANGUAGE;
};

/* resetStore() handles instances where the userPreferences object in local storage is malformed and unable to be parsed. This resets the data and sets a language preference for the current user. */

const resetStorage = async (
  user: User,
  language?: ILanguage | null | undefined,
): Promise<{ updatedPreference: ILanguage }> => {
  const uniqueUserId = user?.unique_id;
  const updatedPreference = await validLanguage(language);
  const value = JSON.stringify({
    [uniqueUserId]: updatedPreference,
  });
  localStorage.setItem(STORAGE_KEY, value);

  return { updatedPreference };
};

export const getLanguagePreference = async (user: User): Promise<ILanguage> => {
  const uniqueUserId = user?.unique_id;

  try {
    const languagePreferences = localStorage.getItem(STORAGE_KEY);

    const currentUserPreference = languagePreferences
      ? (JSON.parse(languagePreferences)?.[uniqueUserId] as ILanguage)
      : DEFAULT_LANGUAGE;

    return await validLanguage(currentUserPreference);
  } catch (_e) {
    const { updatedPreference } = await resetStorage(user);
    return updatedPreference;
  }
};

export const updateLanguagePreference = async (
  user: User,
  language: ILanguage | null | undefined,
) => {
  const uniqueUserId = user?.unique_id;

  try {
    const languagePreferences = localStorage.getItem(STORAGE_KEY);

    const currentPreferences = languagePreferences
      ? JSON.parse(languagePreferences || "")
      : {};

    const value = JSON.stringify({
      ...currentPreferences,
      [uniqueUserId]: await validLanguage(language),
    });

    localStorage.setItem(STORAGE_KEY, value);
  } catch (_e) {
    await resetStorage(user, language);
  }
};
